import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Filter,
  Calendar,
  MapPin,
  Euro,
  Tag,
  FileText,
  Search,
  Bell,
  Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import GuideBreadcrumb from "@/components/GuideBreadcrumb";
import LanguageSelector from "@/components/LanguageSelector";

export default function FiltriRicercaAvanzata() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Guida ai Filtri di Ricerca Avanzata per Bandi Europei"
        description="Scopri come usare i filtri avanzati per trovare bandi europei su TED Monitor: date, paesi, codici CPV, valore stimato, tipo di gara e ente appaltante."
        path="/it/guide/filtri-ricerca-avanzata"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/guide/filtri-ricerca-avanzata" },
          { lang: "x-default", href: "https://tedmonitor.eu/it/guide/filtri-ricerca-avanzata" },
        ]}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", href: "https://tedmonitor.eu/" },
          { name: "Guide", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
          { name: "Filtri di Ricerca Avanzata", href: "https://tedmonitor.eu/it/guide/filtri-ricerca-avanzata" },
        ]}
      />

      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <GuideBreadcrumb
          items={[
            { label: "Guide", href: "/it/guide/consultare-ted" },
            { label: "Filtri di Ricerca Avanzata" },
          ]}
        />
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-4">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Guida
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Guida ai Filtri di Ricerca Avanzata per Bandi Europei
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              TED Monitor offre un sistema di filtri avanzati che ti permette di raffinare la ricerca
              dei bandi europei in modo preciso. Scopri come utilizzare ogni filtro per trovare
              esattamente le opportunità di appalto che ti interessano.
            </p>

            {/* Internal links */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/guide/codici-cpv"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Codici CPV
              </Link>
              <Link
                to="/it/guide/consultare-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Come consultare il TED
              </Link>
              <Link
                to="/it/guide/bandi-vs-preinformazione"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Bandi vs Pre-informazione
              </Link>
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio appalti europei
              </Link>
            </div>
          </div>
        </section>

        {/* Panoramica filtri */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Panoramica dei filtri disponibili
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              I filtri avanzati di TED Monitor ti permettono di combinare più criteri per ottenere
              risultati altamente pertinenti. Puoi usarli sia nella ricerca manuale che nella
              configurazione dei monitoraggi automatici.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { icon: Search, label: "Parole chiave", desc: "Termini di ricerca libera nel titolo e descrizione" },
                { icon: Tag, label: "Codici CPV", desc: "Classificazione merceologica europea standardizzata" },
                { icon: MapPin, label: "Paesi", desc: "Filtra per uno o più stati membri UE" },
                { icon: Calendar, label: "Intervallo di date", desc: "Periodo di pubblicazione dei bandi" },
                { icon: Euro, label: "Valore stimato", desc: "Intervallo minimo e massimo dell'importo" },
                { icon: FileText, label: "Tipo di gara", desc: "Bandi, aggiudicazioni o pre-informazioni" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border">
                  <item.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Parole chiave */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per parole chiave
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Le parole chiave vengono cercate nel titolo e nella descrizione dei bandi pubblicati sul TED.
              Puoi inserire più parole chiave per affinare la ricerca.
            </p>

            <div className="bg-background rounded-lg border p-4 mb-4">
              <p className="font-medium text-sm mb-2 text-foreground">💡 Suggerimenti per le parole chiave:</p>
              <ul className="space-y-1.5 text-muted-foreground text-sm">
                {[
                  "Usa termini specifici del tuo settore (es. \"software gestionale\", \"manutenzione impianti\")",
                  "Prova anche in inglese per catturare bandi multilingue",
                  "Combina parole chiave con codici CPV per risultati più precisi",
                  "Evita termini troppo generici come \"servizi\" o \"forniture\"",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Codici CPV */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Tag className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per codici CPV
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              I codici CPV (Common Procurement Vocabulary) classificano gli appalti in categorie
              standardizzate. TED Monitor supporta la selezione multipla con autocompletamento
              nella tua lingua.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Digita il nome della categoria o il codice numerico per trovare i CPV",
                "Seleziona più codici CPV per coprire categorie correlate",
                "I codici selezionati vengono visualizzati come badge rimovibili",
                "Usa codici generici (prime 2 cifre) per una ricerca più ampia",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="mt-2">
              <Link to="/it/guide/codici-cpv" className="text-primary hover:underline font-medium text-sm">
                Approfondisci la guida ai codici CPV →
              </Link>
            </p>
          </div>
        </section>

        {/* Filtro Paesi */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per paesi
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Puoi filtrare i bandi per uno o più paesi dell'Unione Europea. Per impostazione
              predefinita, TED Monitor rileva automaticamente il tuo paese in base alla posizione,
              ma puoi selezionare "Tutti i paesi" o scegliere manualmente i mercati di tuo interesse.
            </p>

            <div className="bg-background rounded-lg border p-4 mb-4">
              <p className="font-medium text-sm mb-2 text-foreground">🌍 Copertura geografica:</p>
              <p className="text-muted-foreground text-sm">
                TED Monitor copre tutti i <strong>27 stati membri dell'UE</strong> più i paesi dello
                Spazio Economico Europeo (SEE). Puoi monitorare bandi in un singolo paese o in più
                mercati contemporaneamente.
              </p>
            </div>

            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Seleziona uno o più paesi dal menu a tendina",
                "Usa \"Tutti i paesi\" per una ricerca paneuropea",
                "Il rilevamento automatico suggerisce il tuo mercato locale",
                "Combina con parole chiave nella lingua locale per risultati ottimali",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Filtro Date */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Calendar className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per intervallo di date
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il filtro date ti permette di limitare la ricerca a un periodo specifico di
              pubblicazione. Puoi impostare una data di inizio, una data di fine o entrambe.
            </p>

            <div className="bg-background rounded-lg border p-4 mb-4">
              <p className="font-medium text-sm mb-2 text-foreground">⚠️ Attenzione ai monitoraggi:</p>
              <p className="text-muted-foreground text-sm">
                Se imposti un filtro data su un monitoraggio automatico, ricorda che le date fisse
                possono diventare obsolete nel tempo. TED Monitor ti avvisa con un indicatore arancione
                quando un filtro data potrebbe limitare i risultati del monitoraggio.
              </p>
            </div>

            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Imposta \"Data da\" per cercare solo bandi pubblicati dopo una certa data",
                "Imposta \"Data a\" per limitare ai bandi pubblicati prima di una data",
                "Per i monitoraggi, considera di non usare date fisse per ricevere sempre i bandi più recenti",
                "Puoi anche filtrare per scadenza con l'opzione \"Solo con scadenza\"",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Filtro Valore */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Euro className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per valore stimato
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Filtra i bandi in base al valore economico stimato dell'appalto. Puoi impostare
              un importo minimo, massimo o entrambi per concentrarti sulle opportunità della
              dimensione giusta per la tua azienda.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Imposta un valore minimo per escludere bandi troppo piccoli",
                "Imposta un valore massimo per escludere bandi fuori portata",
                "Attiva \"Includi bandi senza valore\" per non perdere bandi che non indicano l'importo",
                "I valori sono espressi in euro (€) indipendentemente dal paese",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="bg-background rounded-lg border p-4">
              <p className="font-medium text-sm mb-2 text-foreground">💡 Consiglio:</p>
              <p className="text-muted-foreground text-sm">
                Molti bandi europei non indicano il valore stimato. Ti consigliamo di attivare
                sempre l'opzione "Includi bandi senza valore" per non perdere opportunità
                potenzialmente interessanti.
              </p>
            </div>
          </div>
        </section>

        {/* Tipo di gara */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileText className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Filtro per tipo di gara
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il TED pubblica diversi tipi di avvisi. Il filtro "Tipo di gara" ti permette di
              distinguere tra le varie tipologie per concentrarti su quelle più rilevanti.
            </p>

            <div className="space-y-3 mb-4">
              {[
                {
                  title: "Bandi di gara (cn-standard)",
                  desc: "Avvisi di gara attivi con scadenza per la presentazione delle offerte. Sono le opportunità concrete su cui puoi partecipare.",
                },
                {
                  title: "Aggiudicazioni (can-standard)",
                  desc: "Risultati delle gare concluse. Utili per analizzare la concorrenza, i prezzi di mercato e le tendenze.",
                },
                {
                  title: "Pre-informazioni",
                  desc: "Avvisi preliminari su gare future. Ti danno un vantaggio competitivo per prepararti in anticipo.",
                },
              ].map((item) => (
                <div key={item.title} className="p-3 bg-muted/30 rounded-lg border">
                  <p className="font-medium text-sm text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <p className="mt-2">
              <Link to="/it/guide/bandi-vs-preinformazione" className="text-primary hover:underline font-medium text-sm">
                Approfondisci le differenze tra bandi e pre-informazioni →
              </Link>
            </p>
          </div>
        </section>

        {/* Combinare i filtri */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Lightbulb className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Strategie per combinare i filtri
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              La potenza dei filtri avanzati sta nella possibilità di combinarli. Ecco alcune
              strategie collaudate per ottenere i migliori risultati:
            </p>

            <div className="space-y-4">
              <div className="bg-background rounded-lg border p-4">
                <p className="font-medium text-sm text-primary mb-1">🎯 Ricerca mirata</p>
                <p className="text-muted-foreground text-sm">
                  <strong>CPV specifici + Paese + Valore minimo</strong> — Ideale per trovare gare
                  concrete nel tuo settore e mercato di riferimento.
                </p>
              </div>

              <div className="bg-background rounded-lg border p-4">
                <p className="font-medium text-sm text-primary mb-1">🔍 Esplorazione di mercato</p>
                <p className="text-muted-foreground text-sm">
                  <strong>CPV generici + Tutti i paesi + Aggiudicazioni</strong> — Perfetto per
                  analizzare il mercato europeo, i competitor e i prezzi di aggiudicazione.
                </p>
              </div>

              <div className="bg-background rounded-lg border p-4">
                <p className="font-medium text-sm text-primary mb-1">⚡ Monitoraggio automatico</p>
                <p className="text-muted-foreground text-sm">
                  <strong>Parole chiave + CPV + Paesi target</strong> — Configura un monitoraggio
                  con alert email per ricevere notifiche sui nuovi bandi senza controllare manualmente.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Monitoraggi e alert */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Dai filtri ai monitoraggi automatici
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Una volta trovata la combinazione di filtri giusta, puoi salvarla come monitoraggio
              automatico. TED Monitor eseguirà la ricerca periodicamente e ti invierà una notifica
              (anche via email) quando trova nuovi bandi che corrispondono ai tuoi criteri.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Configura la frequenza degli alert (giornaliera, settimanale)",
                "Ricevi notifiche in-app e via email",
                "Visualizza lo stato del monitoraggio con indicatori colorati",
                "Modifica i filtri del monitoraggio in qualsiasi momento",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="mt-2">
              <Link to="/it/alert-bandi-ted" className="text-primary hover:underline font-medium text-sm">
                Scopri come configurare gli alert TED →
              </Link>
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Pronto a trovare i bandi giusti per te?
            </h2>
            <p className="text-base mb-6 opacity-90">
              Usa i filtri avanzati di TED Monitor per scoprire le opportunità di appalto europeo
              più rilevanti per la tua azienda.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/fonte-dei-dati" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
